import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Context, Hooks, Util } from "@xt/xtbase";
import { get_inviteVipProgress } from "@/api/referral";
import { levelForReferralCornerBadge } from "@/utils/referralVipCornerBadge";
import { getZenLang } from "@/utils/method";
import { setMarketGroupStorageForCfdCategory } from "@/utils/setCfdMarketDrawerTab";
import styles from "./index.module.less";
import DepositIcon from "./icons/deposit.svg";
import FiatDepositIcon from "./icons/fiatDeposit.svg";
import CryptoDepositIcon from "./icons/cryptoDeposit.svg";
import PartnerIcon from "./icons/partner.svg";
import ReferralIcon from "./icons/referral.svg";
import WatchlistIcon from "./icons/watchlist.svg";
import SpotIcon from "./icons/spot.svg";
import Spot2Icon from "./icons/spot2.svg";
import HotCoinsIcon from "./icons/hotCoins.svg";
import MoreCfdCryptoIcon from "./icons/moreCfdCrypto.svg";
import MoreCfdStocksIcon from "./icons/moreCfdStocks.svg";
import MoreCfdCommoditiesIcon from "./icons/moreCfdCommodities.svg";
import AiCopyIcon from "./icons/aiCopy.svg";
// import AiSelectIcon from "./icons/aiselect.svg";
import CustomerServiceIcon from "./icons/customerService.svg";
import HelpCenterIcon from "./icons/helpCenter.svg";
import ConvertIcon from "./icons/convert.svg";

const { useTranslation } = Hooks;
const { getUrl } = Util;
const { XtContext } = Context;

// 返回图标
const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
    <path d="M8 2L2 8L8 14" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/** More 页 CFD 入口固定交易对（与行情 Crypto / Stocks / Commodities 对应） */
const CFD_CATEGORY_SYMBOL = {
  crypto: "btc_usdt_cfd",
  stocks: "tsla_usdt_cfd",
  commodities: "xag_usdt_cfd",
};

const MoreFeatures = () => {
  const router = useRouter();
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const [referralLevelDisplay, setReferralLevelDisplay] = useState(null);

  const refreshReferralLevelBadge = useCallback(async () => {
    if (loginStatus !== 1) {
      setReferralLevelDisplay(null);
      return;
    }
    try {
      const vip = await get_inviteVipProgress();
      setReferralLevelDisplay(levelForReferralCornerBadge(vip));
    } catch (_) {
      setReferralLevelDisplay(null);
    }
  }, [loginStatus]);

  useEffect(() => {
    refreshReferralLevelBadge();
  }, [refreshReferralLevelBadge]);

  const withReferralCornerBadge = (items) => {
    if (referralLevelDisplay == null) return items;
    const badge = t("home.referralLevelCornerBadge", { n: referralLevelDisplay });
    return items.map((item) =>
      item.id === "referral" ? { ...item, cornerBadge: badge } : item
    );
  };

  const handleBack = () => {
    window.history.back();
  };

  // 当前首页服务
  const currentHomeServices = [
    {
      id: "deposit",
      label: t("more.deposit") || "Deposit",
      icon: DepositIcon,
      url: "/wallet/account/common/deposit",
    },
    {
      id: "partner",
      label: t("more.partner") || "Partner",
      icon: PartnerIcon,
      url: "/affiliate",
    },
    {
      id: "referral",
      label: t("more.referral") || "Referral",
      icon: ReferralIcon,
      url: "/referral",
    },
    {
      id: "watchlist",
      label: t("more.watchlist") || "Watchlist",
      icon: WatchlistIcon,
      url: "/market",
    },
    {
      id: "spot",
      label: t("more.spot") || "Spot",
      icon: SpotIcon,
      url: "/trade/btc_usdt",
    },
  ];

  // 常用功能：Fiat Deposit, Crypto Deposit, Convert, Referral, Partner（与首页跳转逻辑一致）
  const commonFunctions = [
    {
      id: "fiatDeposit",
      label: t("more.fiatDeposit") || t("fiat.fiatDeposit") || "Fiat Deposit",
      icon: CryptoDepositIcon,
      url: "/wallet/account/common/deposit/fiat",
    },
    {
      id: "cryptoDeposit",
      label: t("more.cryptoDeposit") || t("fiat.cryptoDeposit") || "Crypto Deposit",
      icon: FiatDepositIcon,
      url: "/wallet/account/common/deposit",
    },
    {
      id: "convert",
      label: t("more.convert") || "Convert",
      icon: ConvertIcon,
      url: "/futures/contract/spot",
    },
    {
      id: "referral",
      label: t("more.referral") || "Referral",
      icon: ReferralIcon,
      url: "/referral",
    },
    {
      id: "partner",
      label: t("more.partner") || "Partner",
      icon: PartnerIcon,
      url: "/affiliate",
    },
    {
      id: "thirdParty",
      label: t("more.thirdParty") || "Third-party",
      icon: FiatDepositIcon,
      url: "/credit/debitCard",
    },
  ];

  // 交易分类：CFD 合约（固定默认交易对）、AI Trading（与其它入口一致：url + router.push(getUrl)）
  const tradingFunctions = [
    {
      id: "cfdCrypto",
      label: t("market.subTabCrypto") || "Crypto",
      icon: MoreCfdCryptoIcon,
      url: `/futures/contract/${CFD_CATEGORY_SYMBOL.crypto}`,
      cfdCategory: "crypto",
    },
    {
      id: "cfdStocks",
      label: t("market.subTabStocks") || "Stocks",
      icon: MoreCfdStocksIcon,
      url: `/futures/contract/${CFD_CATEGORY_SYMBOL.stocks}`,
      cfdCategory: "stocks",
    },
    {
      id: "cfdCommodities",
      label: t("market.subTabCommodities") || "Commodities",
      icon: MoreCfdCommoditiesIcon,
      url: `/futures/contract/${CFD_CATEGORY_SYMBOL.commodities}`,
      cfdCategory: "commodities",
    },
    {
      id: "aiCopy",
      label: t("more.aiCopy") || "AI Trading",
      icon: AiCopyIcon,
      url: "/aicopy",
    },
    // {
    //   id: "aiselect",
    //   label: t("more.aiSelect") || "AI Select",
    //   icon: AiSelectIcon,
    //   url: "/aiselect",
    // },
  ];

  // 帮助与客服
  const helpAndSupport = [
    {
      id: "customerService",
      label: t("more.customerService") || "Support",
      icon: CustomerServiceIcon,
      url: "#",
      onClick: () => {
        window.open("https://j5wxcf.7mmon3ss.com/chatwindow.aspx?siteId=65001365&planId=e146e265-fd7b-4e13-a3d3-76908e2394b3", "_blank", "width=800,height=600,scrollbars=yes,resizable=yes");
      },
    },
    {
      id: "helpCenter",
      label: t("more.helpCenter") || "Help Center",
      icon: HelpCenterIcon,
      url: "#",
      onClick: () => {
        window.open(`https://spikex.zendesk.com/hc/${getZenLang()}`, "_blank");
      },
    },
  ];

  const handleItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (item.url && item.url !== "#") {
      if (item.cfdCategory) {
        setMarketGroupStorageForCfdCategory(item.cfdCategory);
      }
      router.push(getUrl(item.url));
    }
  };

  const renderSection = (title, items, className = "") => (
    <div className={className}>
      <h3 className={styles["more-features__section-title"]}>{title}</h3>
      <div className={styles["more-features__items"]}>
        {items.map((item) => (
          <button
            key={item.id}
            className={styles["more-features__item"]}
            onClick={() => handleItemClick(item)}
          >
            <div
              className={
                item.iconSize === "sm"
                  ? `${styles["more-features__icon-wrapper"]} ${styles["more-features__icon-wrapper--sm"]}`
                  : styles["more-features__icon-wrapper"]
              }
            >
              <img src={item.icon} alt={item.label} className={styles["more-features__icon"]} />
              {item.cornerBadge ? (
                <span className={styles["more-features__corner-badge"]}>{item.cornerBadge}</span>
              ) : null}
            </div>
            <span className={styles["more-features__label"]}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles["more-features"]}>
      <div className={styles["more-features__header"]}>
        <button className={styles["more-features__back"]} onClick={handleBack}>
          <BackIcon />
        </button>
        <h1 className={styles["more-features__title"]}>
          {t("more.title") || "All Features"}
        </h1>
      </div>

      <div className={styles["more-features__content"]}>
        {/* {renderSection(
          t("more.currentHomeServices") || "Current Home Services",
          withReferralCornerBadge(currentHomeServices),
          styles["more-features__section"]
        )} */}

        {renderSection(
          t("more.commonFunctions") || "Common Functions",
          withReferralCornerBadge(commonFunctions),
          styles["more-features__section"]
        )}

        {renderSection(
          t("more.trading") || "Trading",
          tradingFunctions,
          styles["more-features__section"]
        )}

        {renderSection(
          t("more.helpAndSupport") || "Help & Support",
          helpAndSupport,
          styles["more-features__section"]
        )}
      </div>
    </div>
  );
};

export default MoreFeatures;
